import React, { useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import beSaveTimeOffAllowance from 'actions/timeoff/beSaveTimeOffAllowance.action';
import StaffAllowanceInput from 'components/hr/timeOffStaff/StaffAllowanceInput.component';
import S from 'constants/string.const';
import { selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import { selectTimeOffAllowances, selectTimeOffCategories } from 'selectors/timeoff.selectors';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import call from 'utils/functions/call.util';
import asObject from 'utils/objects/asObject.util';
import isFalsy from 'utils/predicates/isFalsy.util';
import C from 'utils/values/constant.util';

import CN from './StaffTimeOffAllowances.module.scss';

const ALERTS = C({
  success: 'success',
  info: 'info',
  danger: 'danger',
});

const ALERT_MESSAGE = C({
  processing: 'Allowance is being saved...',
  save: 'Allowance saved!',
  saveFail: [
    'Time off allowances can only be saved as whole numbers or .5 numbers.',
    'Please set your allowance to a whole number or a number ending in .5.',
  ].join(S.space),
  error: 'Something went wrong. Please try again or contact customer support if this keeps happening.',
});

const getExistingAllowanceId = ({ allowances, currAllowance }) => {
  const { time_off_category_id: categoryId, stylist_id: stylistId } = asObject(currAllowance);

  const found = allowances.find(
    ({ time_off_category_id: allowanceCategoryId, stylist_id: allowanceStylistId }) =>
      allowanceCategoryId === categoryId && allowanceStylistId === stylistId,
  );

  return asObject(found).id;
};

const StaffTimeOffAllowance = ({
  salonId,
  allowance,
  stylist,
  index,
  onAlert,
  categories: categoriesFromProps,
  allowances: allowancesFromProps,
  beSaveTimeOffAllowance,
}) => {
  const categories = asArray(categoriesFromProps);
  const allowances = asArray(allowancesFromProps);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const handleSelect = ({ index }) => void setSelectedIndex(index);

  const handleBlur = ({ allowance, stylist, category, isDirty }) => {
    setSelectedIndex();
    if (!isDirty) return;

    const currAllowance = {
      time_off_category_id: category.id,
      stylist_id: stylist.id,
      allowance: fromAnyToNumber(allowance.allowance),
    };

    const existingAllowanceId = getExistingAllowanceId({ allowances, currAllowance });

    const options = {
      salonId,
      isNew: isFalsy(existingAllowanceId),
      allowance: { ...currAllowance, id: existingAllowanceId },
    };

    call('onAlert', onAlert, { type: ALERTS.info, message: ALERT_MESSAGE.processing });

    beSaveTimeOffAllowance(options)
      .then(({ response, error }) => {
        if (!response && !error) return;

        const options = error
          ? { type: ALERTS.danger, message: ALERT_MESSAGE.saveFail }
          : { type: ALERTS.success, message: ALERT_MESSAGE.save };

        call('onAlert', onAlert, options);
      })
      .catch((e) => {
        call('onAlert', onAlert, { type: ALERTS.danger, message: ALERT_MESSAGE.error });
        throw e;
      });
  };

  return (
    <div
      data-bem="StaffTimeOffAllowances"
      className={cn({
        [CN.stylistRow]: true,
        [CN.focusIn]: index === selectedIndex,
      })}
    >
      <div data-bem="StaffTimeOffAllowances__list" className={CN.list}>
        <div className={CN.staffName}>{stylist.user_name}</div>
        {categories.map((c) => (
          <div key={c.id}>
            <StaffAllowanceInput
              index={index}
              allowance={allowance[c.id]}
              category={c}
              stylist={stylist}
              onChange={handleSelect}
              onBlur={handleBlur}
              onFocus={handleSelect}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
  categories: selectTimeOffCategories(state),
  allowances: selectTimeOffAllowances(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ beSaveTimeOffAllowance }, dispatch);

const StaffTimeOffAllowanceHoc = connect(mapStateToProps, mapDispatchToProps)(StaffTimeOffAllowance);

export default StaffTimeOffAllowanceHoc;
